// Фоновый drain очереди tutor-сообщений (offline outbox).
// Монтируется внутри AuthProvider + api.Provider — нужен trpcMutation + auth.
// Вне React-дерева чата: очередь живёт при закрытой панели / другом роуте.

import { toast } from "@studyhub/ui/toast";
import { type ReactNode, useEffect } from "react";
import i18n from "@/i18n";
import { trpcMutation } from "@/lib/trpc-fetch";
import {
	type DroppedTutorMessage,
	getTutorMessageOutbox,
	resetTutorMessageOutboxForTests,
} from "@/lib/tutor-message-outbox";
import { useAuth } from "./auth-provider";

/**
 * Коллизия clientMessageId означает, что сервер это сообщение УЖЕ принял.
 * Выбросить его из очереди правильно, и это не потеря — уведомлять не о чем.
 */
const ALREADY_DELIVERED = "MESSAGE_ID_COLLISION";

function isPermanentEnqueueError(err: unknown): boolean {
	const msg = err instanceof Error ? err.message : String(err);
	// QUOTA_EXCEEDED — временная (суточный лимит), НЕ дропаем текст пользователя.
	// NOT_FOUND НЕ permanent: офлайн-сессия может ещё не доехать через PowerSync.
	// READ_ONLY_MODE — временный grace после downgrade.
	return msg.includes(ALREADY_DELIVERED);
}

/**
 * Видимая деградация: написанный пользователем текст исчез безвозвратно.
 * Раньше такие сообщения просто удалялись из IDB без единого следа — очередь
 * аудио-чанков в том же положении показывает toast. Текст кладём в описание и
 * даём скопировать, чтобы его можно было отправить заново.
 */
function notifyDropped(dropped: readonly DroppedTutorMessage[]): void {
	const lost = dropped.filter((d) => !d.reason.includes(ALREADY_DELIVERED));
	if (lost.length === 0) return;
	for (const item of lost) {
		// eslint-disable-next-line no-console
		console.error("tutor-message-outbox: message dropped permanently", {
			clientMessageId: item.clientMessageId,
			sessionId: item.sessionId,
			reason: item.reason,
		});
	}
	const first = lost[0];
	toast.error(i18n.t("outbox.dropped", { ns: "tutor", count: lost.length }), {
		description: first?.text,
		action: first
			? {
					label: i18n.t("outbox.droppedCopy", { ns: "tutor" }),
					onClick: () => {
						void navigator.clipboard?.writeText(lost.map((d) => d.text).join("\n\n"));
					},
				}
			: undefined,
	});
}

export function TutorOutboxProvider({ children }: { children: ReactNode }) {
	const { isAuthenticated, isLoading, user } = useAuth();
	const userId = user?.id;

	useEffect(() => {
		if (isLoading || !isAuthenticated || !userId) {
			resetTutorMessageOutboxForTests();
			return;
		}

		const outbox = getTutorMessageOutbox({
			userId,
			send: async (item) => {
				await trpcMutation<{ userMessageId: string; alreadyExisted: boolean }>(
					"tutor.messages.enqueueSend",
					{
						sessionId: item.sessionId,
						text: item.text,
						clientMessageId: item.clientMessageId,
					},
				);
			},
			isPermanentError: isPermanentEnqueueError,
			onDropped: notifyDropped,
		});

		return outbox.startBackground();
	}, [isAuthenticated, isLoading, userId]);

	return <>{children}</>;
}
